import React, { useCallback, useEffect, useState } from 'react';
import { Users, Save, Sparkles, Megaphone, Download, Copy, Check, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin control: "Partnered with [run club]" credit per City Series tee.
export default function RunClubPartnersCard() {
  const token = sessionStorage.getItem('admin_token');
  const [cities, setCities] = useState([]);
  const [clubs, setClubs] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [posts, setPosts] = useState([]);
  const [copied, setCopied] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadPosts = useCallback(() => {
    fetch(`${API_URL}/api/admin/city-series/launch-posts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setPosts(d.posts || []))
      .catch(console.error);
  }, [token]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/city-series/run-clubs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        setCities(d.cities || []);
        setSuggestions(d.suggestions || {});
        const norm = {};
        Object.entries(d.clubs || {}).forEach(([k, v]) => {
          norm[k] = typeof v === 'string' ? { name: v, instagram: '' } : { name: v?.name || '', instagram: v?.instagram || '' };
        });
        setClubs(norm);
      } catch (err) { console.error('Fetch run clubs failed:', err); }
    })();
  }, [token]);

  const applySuggestion = (key) => {
    const s = suggestions[key];
    if (s) setClubs((prev) => ({ ...prev, [key]: { name: s.name, instagram: s.instagram } }));
  };

  const fillAllEmpty = () => {
    setClubs((prev) => {
      const next = { ...prev };
      Object.entries(suggestions).forEach(([k, s]) => {
        if (!next[k]?.name) next[k] = { name: s.name, instagram: s.instagram };
      });
      return next;
    });
  };

  const downloadPost = async (p) => {
    try {
      const res = await fetch(`${API_URL}${p.image_path}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ninjathlete-x-${p.key}-launch.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { console.error(e); }
  };

  const copyCaption = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const buildDm = (c) => {
    const club = clubs[c.key]?.name || suggestions[c.key]?.name || 'your crew';
    const kitUrl = `${window.location.origin}/partner-kit/${c.key}`;
    return `Hey ${club} 👋 NinjAthlete here. We designed a limited ${c.city} tee for our City Series drop — "${c.design}" (${c.tagline}) — and we want ${club} to be the official partner club on it. Free partner credit on the tee page with a tap-through to your IG, plus a co-branded launch post for both accounts to share. We put your launch kit together here: ${kitUrl} — take a look, and if you're in just reply here 🖤`;
  };

  const DmButton = ({ c }) => (
    <button
      onClick={() => copyCaption(`dm-${c.key}`, buildDm(c))}
      className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-teal-50 text-gray-500 hover:text-teal-700 text-[10px] font-semibold transition-colors"
      title="Copy an Instagram outreach DM with their launch kit link"
      data-testid={`run-club-copy-dm-${c.key}`}
    >
      {copied === `dm-${c.key}` ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Send className="w-3 h-3" /> Copy DM</>}
    </button>
  );

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/city-series/run-clubs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clubs }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      const norm = {};
      Object.entries(d.clubs || {}).forEach(([k, v]) => {
        norm[k] = typeof v === 'string' ? { name: v, instagram: '' } : { name: v?.name || '', instagram: v?.instagram || '' };
      });
      setClubs(norm);
      const n = Object.keys(d.clubs || {}).length;
      setMsg(`Saved — ${n} ${n === 1 ? 'city has' : 'cities have'} a run club credit live on /city-series.`);
      loadPosts();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="run-club-partners-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-lime-600" />
        <h3 className="text-sm font-bold text-gray-900">City Series — Run Club Partners</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        As you lock in a local run club for a city, add it here — the tee card shows "Partnered with [club]" instantly. Add the club's Instagram URL to make the credit tap through for cross-promotion. Leave blank to hide.
      </p>
      <div className="mb-4 p-3 rounded-lg bg-amber-50/70 border border-amber-100 flex items-start justify-between gap-3" data-testid="run-club-suggestions-note">
        <p className="text-[11px] text-amber-800">
          <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />
          <strong>Draft suggestions loaded</strong> — we researched the most prominent run club in each city. Tap "Use" to fill a row (or fill all), verify the club + IG handle, then hit Save to publish. Nothing goes live until you save.
        </p>
        <button
          onClick={fillAllEmpty}
          className="shrink-0 px-2.5 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-[10px] font-bold transition-colors"
          data-testid="run-club-fill-all-btn"
        >
          Fill all empty
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-2 mb-4">
        {cities.map((c) => (
          <div key={c.key}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700 w-24 shrink-0">{c.city}</span>
              <input
                type="text"
                value={clubs[c.key]?.name || ''}
                onChange={(e) => setClubs((prev) => ({ ...prev, [c.key]: { ...(prev[c.key] || {}), name: e.target.value } }))}
                placeholder="Run club name"
                className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-lime-500"
                data-testid={`run-club-input-${c.key}`}
              />
              <input
                type="text"
                value={clubs[c.key]?.instagram || ''}
                onChange={(e) => setClubs((prev) => ({ ...prev, [c.key]: { ...(prev[c.key] || {}), instagram: e.target.value } }))}
                placeholder="Instagram URL"
                className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-pink-400"
                data-testid={`run-club-ig-input-${c.key}`}
              />
            </div>
            {!clubs[c.key]?.name && suggestions[c.key] && (
              <div className="flex items-center gap-1.5 mt-0.5 ml-[104px]">
                <span className="text-[10px] text-gray-400 truncate">Suggested: {suggestions[c.key].name}</span>
                <button
                  onClick={() => applySuggestion(c.key)}
                  className="shrink-0 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-lime-100 text-gray-500 hover:text-lime-700 text-[10px] font-semibold transition-colors"
                  data-testid={`run-club-use-suggestion-${c.key}`}
                >
                  Use
                </button>
                <DmButton c={c} />
              </div>
            )}
            {clubs[c.key]?.name && (
              <div className="flex items-center gap-1.5 mt-0.5 ml-[104px]">
                <DmButton c={c} />
              </div>
            )}
          </div>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg disabled:opacity-50"
        data-testid="run-club-save-btn"
      >
        <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Partners'}
      </button>
      {msg && <p className="text-xs text-gray-600 mt-3" data-testid="run-club-msg">{msg}</p>}

      {posts.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100" data-testid="launch-posts-section">
          <div className="flex items-center gap-2 mb-1">
            <Megaphone className="w-4 h-4 text-lime-600" />
            <h3 className="text-sm font-bold text-gray-900">Partner Launch Posts</h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Auto-generated 1080×1080 announcement graphic + captions for every saved partner — one caption for @ninjathlete, one ready to send the club so both accounts can post.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {posts.map((p) => (
              <div key={p.key} className="flex gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-testid={`launch-post-${p.key}`}>
                <img
                  src={`${API_URL}${p.image_path}?v=${encodeURIComponent(p.club)}`}
                  alt={`${p.city} launch post`}
                  className="w-20 h-20 rounded object-cover border border-gray-200 flex-shrink-0"
                  loading="lazy"
                  data-testid={`launch-post-img-${p.key}`}
                />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-800 truncate">{p.city} × {p.club}</p>
                  <p className="text-[10px] text-gray-400 mb-1.5 truncate">"{p.design}" {p.handle && `· ${p.handle}`}</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => downloadPost(p)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-900 text-white text-[10px] font-semibold" data-testid={`launch-post-download-${p.key}`}>
                      <Download className="w-3 h-3" /> PNG
                    </button>
                    <button onClick={() => copyCaption(`${p.key}-ninja`, p.captions.ninja)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-lime-100 hover:bg-lime-200 text-lime-800 text-[10px] font-semibold" data-testid={`launch-post-copy-ninja-${p.key}`}>
                      {copied === `${p.key}-ninja` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Our caption
                    </button>
                    <button onClick={() => copyCaption(`${p.key}-club`, p.captions.club)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 text-[10px] font-semibold" data-testid={`launch-post-copy-club-${p.key}`}>
                      {copied === `${p.key}-club` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Club caption
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
